import { useEffect, useState } from 'react'

export interface ClockState {
    text: string
    hours: number
    minutes: number
    seconds: number
    offset: string
    ready: boolean
}

const formatters = new Map<string, Intl.DateTimeFormat>()

const getFormatter = (timeZone: string): Intl.DateTimeFormat => {
    const cached = formatters.get(timeZone)

    if (cached) return cached

    const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
    })

    formatters.set(timeZone, formatter)

    return formatter
}

const readOffset = (timeZone: string, date: Date): string => {
    try {
        const parts = new Intl.DateTimeFormat('en-GB', { timeZone, timeZoneName: 'shortOffset' }).formatToParts(date)
        const name = parts.find(part => part.type === 'timeZoneName')?.value ?? ''

        if (name === 'GMT') return 'UTC+0'

        return name.replace('GMT', 'UTC')
    }
    catch {
        return ''
    }
}

const read = (timeZone: string, withSeconds: boolean): ClockState => {
    const now = new Date()
    const parts = getFormatter(timeZone).formatToParts(now)
    const pick = (type: string): number => Number(parts.find(part => part.type === type)?.value ?? 0)

    const hours = pick('hour') % 24
    const minutes = pick('minute')
    const seconds = pick('second')
    const pad = (value: number): string => String(value).padStart(2, '0')

    return {
        text: withSeconds ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(hours)}:${pad(minutes)}`,
        hours,
        minutes,
        seconds,
        offset: readOffset(timeZone, now),
        ready: true
    }
}

export const useClock = (timeZone: string, withSeconds: boolean = true): ClockState => {
    const [state, setState] = useState<ClockState>({
        text: withSeconds ? '--:--:--' : '--:--',
        hours: 0,
        minutes: 0,
        seconds: 0,
        offset: '',
        ready: false
    })

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined

        const tick = (): void => {
            setState(read(timeZone, withSeconds))
            timer = setTimeout(tick, 1000 - (Date.now() % 1000) + 5)
        }

        tick()

        return () => {
            if (timer) clearTimeout(timer)
        }
    }, [timeZone, withSeconds])

    return state
}
